import { Divider } from '@heroui/react';
import React, { useEffect, useState } from 'react';
import { AdditionalFilters } from '../../components/additional-filters/AdditionalFilters';
import { IAdditionalFilter, IDataField, IDataOption, IFileInfo, IStoppageEventsState, ReportType } from '../../types/reportsTypes';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import { selectDateRangeBoundaries } from '../../selectors/downloadSelectors';
import { CalendarDate } from '@internationalized/date';
import { DataSelection } from '../../components/data-selection/DataSelection';
import { DataPreview } from '../../components/data-preview/DataPreview';
import { selectSelectedVesselsImoNumbers, selectSelectedVesselsSectors } from '../../../../shared/sharedSelectors';
import { selectGeneralSeaAreas } from '../../slices/generalSeaAreasSlice';
import { areaSignalStatisticsActions, selectAreaSignalStatistics } from '../../slices/areaSignalStatisticsSlice';
import { formatDateAsString } from '../../../../utils/helpers';
import { calculateDataExportEstimation } from '../../utils/downloadHelpers';
import { TReportRequestParams } from '../../types/downloadTypes';
import { activityReportActions } from '../../slices/activityReportSlice';
import { addSeascapeLogActivity } from '../../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../../activity-logging/activityLogTypes';

const StoppageEvents: React.FC = () => {
	const dispatch = useAppDispatch();
	
	const dateFormatOptions: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	};

	const { firstDayOfGivenMonthOrLastMonth, lastDayOfGivenMonthOrLastMonth } = useAppSelector(selectDateRangeBoundaries);
	const selectedVesselsImoNumbers = useAppSelector(selectSelectedVesselsImoNumbers);
	const generalSeaAreas = useAppSelector(selectGeneralSeaAreas);
	const areaSignalStatistics = useAppSelector(selectAreaSignalStatistics);
	const selectedVesselsSectors = useAppSelector(selectSelectedVesselsSectors);

	const getInitialState = (): IStoppageEventsState => ({
		additionalFilters: {
			imoNumbers: [],
			sectors: [],
			dateRange: {
				start: new CalendarDate(
					firstDayOfGivenMonthOrLastMonth.getFullYear(),
					firstDayOfGivenMonthOrLastMonth.getMonth() + 1,
					firstDayOfGivenMonthOrLastMonth.getDate(),
				),
				end: new CalendarDate(
					lastDayOfGivenMonthOrLastMonth.getFullYear(),
					lastDayOfGivenMonthOrLastMonth.getMonth() + 1,
					lastDayOfGivenMonthOrLastMonth.getDate(),
				),
			},
			location: {
				type: 'all',
				coordinates: {
					lat: null,
					lon: null,
					latTo: null,
					lonTo: null,
				},
				areaId: [],
				polygonId: undefined,
			},
			duration: {
				min: null,
				max: null,
			},
			ballastLaden: {
				label: '',
				value: '',
			},
			designDraft: {
				min: null,
				max: null,
			},
			activity: {
				label: '',
				value: '',
			},
		},
		dataFields: [
			{ id: 'from_date', label: 'From', selected: false, columnSizeCsv: 20, order: 1, category: 'time', groupOrder: 2 },
			{ id: 'to_date', label: 'To', selected: false, columnSizeCsv: 20, order: 2, category: 'time', groupOrder: 2 },
			{
				id: 'time_days',
				label: 'Duration (Days)',
				selected: false,
				columnSizeCsv: 15,
				order: 3,
				category: 'time',
				groupOrder: 2,
			},
			{ id: 'latitude', label: 'Latitude', selected: false, columnSizeCsv: 12, order: 4, category: 'location', groupOrder: 1 },
			{ id: 'longitude', label: 'Longitude', selected: false, columnSizeCsv: 12, order: 5, category: 'location', groupOrder: 1 },
			{ id: 'general_sea_area', label: 'Sea Area', selected: false, columnSizeCsv: 15, order: 6, category: 'location', groupOrder: 1 },
			{ id: 'polygon_name', label: 'Polygon', selected: false, columnSizeCsv: 15, order: 7, category: 'location', groupOrder: 1 },
			{ id: 'port_name', label: 'Port', selected: false, columnSizeCsv: 20, order: 8, category: 'location', groupOrder: 1 },
			{ id: 'country', label: 'Country', selected: false, columnSizeCsv: 15, order: 9, category: 'location', groupOrder: 1 },
			{ id: 'region', label: 'Region', selected: false, columnSizeCsv: 15, order: 10, category: 'location', groupOrder: 1 },
			{ id: 'eca_sea_area', label: 'ECA Area', selected: false, columnSizeCsv: 15, order: 11, category: 'location', groupOrder: 1 },
			{
				id: 'draught_in',
				label: 'Draft In',
				selected: false,
				columnSizeCsv: 15,
				order: 12,
				category: 'vessel',
				groupOrder: 3,
			},
			{
				id: 'draught_out',
				label: 'Draft Out',
				selected: false,
				columnSizeCsv: 15,
				order: 13,
				category: 'vessel',
				groupOrder: 3,
			},
			{
				id: 'percent_design_draught_in',
				label: '% Design Draft In',
				selected: false,
				columnSizeCsv: 15,
				order: 14,
				category: 'vessel',
				groupOrder: 3,
			},
			{
				id: 'percent_design_draught_out',
				label: '% Design Draft Out',
				selected: false,
				columnSizeCsv: 15,
				order: 15,
				category: 'vessel',
				groupOrder: 3,
			},
			{
				id: 'loaded_condition',
				label: 'Ballast / Laden',
				selected: false,
				columnSizeCsv: 15,
				order: 16,
				category: 'vessel',
				groupOrder: 3,
			},
			{
				id: 'vessel_activity',
				label: 'Activity',
				selected: false,
				columnSizeCsv: 15,
				order: 17,
				category: 'vessel',
				groupOrder: 3,
			},
		], // Static data fields based on screenshot - will be populated from API later
		fileInfo: {
			recordCount: 0,
			vesselCount: 0,
			firstRecord: '-',
			lastRecord: '-',
			estimatedSize: '0.00 MB',
			fileType: 'parquet',
		},
	});

	const [state, setState] = useState<IStoppageEventsState>(getInitialState());
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false); // TODO: Will be used when implementing API data loading

	const updateVesselData = () => {
		// If location type is latlong, dispatch area signal statistics request
		if (state.additionalFilters.location!.type === 'latlong' && state.additionalFilters.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			} else {
				return;
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(adjustedVesselCount);

		setState((prev) => ({
			...prev,
			additionalFilters: {
				...prev.additionalFilters,
				imoNumbers: selectedVesselsImoNumbers,
				sectors: selectedVesselsSectors,
			},
			fileInfo: {
				...prev.fileInfo,
				vesselCount: selectedVesselsImoNumbers.length,
				firstRecord: formatDateAsString(state.additionalFilters.dateRange!.start?.toString(), dateFormatOptions) ?? '-',
				lastRecord: formatDateAsString(state.additionalFilters.dateRange!.end?.toString(), dateFormatOptions) ?? '-',
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleLoadData = () => {
		updateVesselData();
		setIsDataLoaded(true);
	};

	// Set up event listener for the Load button
	useEffect(() => {
		const handleLoadEvent = () => {
			handleLoadData();
		};

		window.addEventListener('activity-report:load', handleLoadEvent);

		return () => {
			window.removeEventListener('activity-report:load', handleLoadEvent);
		};
	}, [state]); // Re-add event listener when state changes to capture latest state

	// Handler functions for state updates
	const handleAdditionalFiltersChange = (filters: IAdditionalFilter) => {
		if (filters.location!.type === 'latlong' && !filters.location!.coordinates) {
			// If location type is latlong but no coordinates are provided, reset the coordinates
			filters.location!.coordinates = {
				lat: -90,
				latTo: 90,
				lon: -180,
				lonTo: 180,
			};
		}

		setState((prev) => ({
			...prev,
			additionalFilters: filters,
		}));
		setIsDataLoaded(false); // Reset data loaded state when filters change
	};

	const handleDataFieldsChange = (dataFields: IDataField[]) => {
		setState((prev) => ({
			...prev,
			dataFields,
		}));
		setIsDataLoaded(false); // Reset data loaded state when data fields change
	};

	const calculateAdjustedVesselCount = (locationOverride?: IAdditionalFilter['location']): number => {
		let adjustedVesselCount = selectedVesselsImoNumbers.length;
		const location = locationOverride || state.additionalFilters.location;

		// If location type is seaArea, filter vessel count according to signalPercent
		if (location!.type === 'seaArea' && location!.areaId && generalSeaAreas) {
			const selectedAreaIds = location!.areaId;

			// Find the selected sea areas and calculate sum of signal percentages
			const selectedSeaAreas = generalSeaAreas.filter((area) => selectedAreaIds.includes(area.name));

			if (selectedSeaAreas.length > 0) {
				// Calculate sum of signal percentages across selected areas
				const totalSignalPercent = selectedSeaAreas.reduce((sum, area) => sum + area.signalPercent, 0);

				// Calculate adjusted vessel count based on the sum of signal percentages
				adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (totalSignalPercent / 100));
			}
		} else if (location!.type === 'latlong' && areaSignalStatistics && areaSignalStatistics.percent) {
			// For latlong, we'll use the response from area signal statistics (handled in useEffect)
			adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (areaSignalStatistics.percent / 100));
		}

		return adjustedVesselCount * 2.25;
	};

	const calculateEstimationWithAdjustments = (
		overrideVesselCount?: number,
		dateRangeOverride?: IAdditionalFilter['dateRange'],
		dataFieldsOverride?: IDataField[],
		frequencyOverride?: IDataOption['frequency'],
		fileTypeOverride?: string,
		locationOverride?: IAdditionalFilter['location'],
	): ReturnType<typeof calculateDataExportEstimation> => {
		const vesselCount = overrideVesselCount ?? calculateAdjustedVesselCount(locationOverride);
		return calculateDataExportEstimation(
			vesselCount,
			dataFieldsOverride || state.dataFields,
			dateRangeOverride || state.additionalFilters.dateRange!,
			frequencyOverride || 'monthly',
			fileTypeOverride || state.fileInfo.fileType,
		);
	};

	const handleFileInfoChange = (fileInfo: IFileInfo) => {
		setState((prev) => ({
			...prev,
			fileInfo,
		}));

		// If location type is latlong, dispatch area signal statistics request to ensure we have updated data
		if (state.additionalFilters.location!.type === 'latlong' && state.additionalFilters.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area signal statistics request
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(
			adjustedVesselCount, // use calculated vessel count
			undefined, // use current date range
			undefined, // use current data fields
			undefined, // use current frequency
			fileInfo.fileType,
		);

		setState((prev) => ({
			...prev,
			fileInfo: {
				...fileInfo,
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleDownload = () => {
		// Create activity report request params from the current state
		// Set end date to end of day (23:59:59) for proper range coverage
		const endDate = state.additionalFilters.dateRange!.end.toDate('UTC');
		endDate.setUTCHours(23, 59, 59, 999);

		const requestParams: TReportRequestParams = {
			reportType: ReportType.StoppageEvents,
			imoList: state.additionalFilters.imoNumbers,
			sectors: state.additionalFilters.sectors!,
			startDate: state.additionalFilters.dateRange!.start.toDate('UTC').toISOString() || '',
			endDate: endDate.toISOString() || '',
			requestedColumns: state.dataFields.filter((field) => field.selected).map((field) => field.id),
			minLat:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lat || -90)) ||
				null,
			maxLat:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.latTo || 90)) ||
				null,
			minLong:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lon || -180)) ||
				null,
			maxLong:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lonTo || 180)) ||
				null,
			seaArea:
				(state.additionalFilters.location!.type === 'seaArea' &&
					state.additionalFilters.location!.areaId?.length &&
					state.additionalFilters.location!.areaId[0]) ||
				'',
			polygonIds: (() => {
				const polygonIds: string[] = [];

				// Add polygon IDs from direct polygon selection
				if (state.additionalFilters.location!.type === 'polygon' && state.additionalFilters.location!.polygonId?.length) {
					polygonIds.push(...state.additionalFilters.location!.polygonId);
				}

				// Add polygon IDs from port selection
				if (state.additionalFilters.location!.type === 'port' && state.additionalFilters.location!.portPolygonIds?.length) {
					polygonIds.push(...state.additionalFilters.location!.portPolygonIds);
				}

				return polygonIds;
			})(),
			durationMin: state.additionalFilters.duration?.min || null,
			durationMax: state.additionalFilters.duration?.max || null,
			ballastLadenStatus: state.additionalFilters.ballastLaden?.value || null,
			designDraftMin: state.additionalFilters.designDraft?.min || null,
			designDraftMax: state.additionalFilters.designDraft?.max || null,
			activities: state.additionalFilters.activity?.value
				? state.additionalFilters.activity.value.split(',').filter((v) => v.trim() !== '')
				: null,
			fileType: state.fileInfo.fileType,
		};

		const payload = JSON.stringify({ DataType: 'StoppageEvents', RequestedParams: requestParams });

		addSeascapeLogActivity({ eventId: SeascapeActivityLogConstants.MSI_DATA_DOWNLOAD, payload });
				
		// console.log('Stoppage Events Report Request Params:', requestParams);
		// Dispatch the request action
		dispatch(activityReportActions.submitActivityReportRequestAction(requestParams));
	};

	return (
		<div className="stoppage-events-report-container flex w-full h-full gap-4">
			{/* Left panel: Additional Filters */}
			<div className="w-2/3 h-full">
				<AdditionalFilters
					filters={state.additionalFilters}
					onChange={handleAdditionalFiltersChange}
					maxDate={lastDayOfGivenMonthOrLastMonth}
					reportType={ReportType.StoppageEvents}
				/>
			</div>

			<Divider orientation="vertical" />

			{/* Middle panel: Data Selection */}
			<div className="w-full h-full">
				<DataSelection dataFields={state.dataFields} onDataFieldsChange={handleDataFieldsChange} />
			</div>

			<Divider orientation="vertical" />

			{/* Right panel: Data Preview */}
			<div className="w-2/3 h-full">
				<DataPreview
					fileInfo={state.fileInfo}
					onFileInfoChange={handleFileInfoChange}
					isLoading={false}
					onDownload={handleDownload}
					isDataLoaded={isDataLoaded}
				/>
			</div>
		</div>
	);
};

export default StoppageEvents;
